import {
    ColumnMetadata,
    ColumnTypes,
    DataGrid,
    DataGridRow,
    RowSizes,
} from "~/components/DataGrid";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import { Anchor } from "~/components/Anchor";
import { ExternalLinkIcon } from "lucide-react";
import { TopTransfer, TopTransferColumns } from "./types";
import { Link } from "~/components/Link";
import { getAssetURL } from "~/utils/getAssetURL";
import { RWA_COIN_ICON } from "~/consts";
import { ImageLoader } from "~/components/ImageLoader";
import { getNetworkURL } from "~/routes";
import { NetworkBadge } from "~/modules/assets/components/AssetTable/NetworkBadge";
import { TopTransfersUserEvents } from "~/modules/analytics/events/topTransfers";
import { useInMemoryTopTransferSort } from "./useInMemoryTopTransferSort";
import { Tooltip } from "~/components/Tooltip";
import {
    getBlockchainExplorerUrl,
    EXPLORER_NAMES,
} from "~/components/BlockchainExplorerLink";
import { Networks } from "~/schema";

// // // //

const columns: ColumnMetadata<TopTransfer, TopTransferColumns>[] = [
    {
        slug: "rank",
        label: "#",
        type: ColumnTypes.number,
        width: 50,
        maxWidth: 50,
        cellValue: (transaction) => {
            return transaction.rank;
        },
    },
    {
        label: "Asset",
        slug: TopTransferColumns.asset,
        sortsOn: TopTransferColumns.asset,
        type: ColumnTypes.string,
        width: 100,
        render: (tx) => {
            return (
                <Link
                    href={getAssetURL({
                        asset: {
                            ticker: tx.asset_ticker,
                            slug: tx.asset_ticker,
                        },
                    })}
                    variant="default"
                    className="w-full flex items-center gap-1.5"
                    analyticsLabel={TopTransfersUserEvents.AssetLinkClick}
                    analyticsEventProperties={{
                        asset: tx.asset_ticker,
                    }}
                >
                    <ImageLoader
                        src={tx.asset_icon || RWA_COIN_ICON}
                        alt={`${tx.asset_name} Icon`}
                        fallbackSrc={RWA_COIN_ICON}
                        className="h-4 w-4 min-w-4 rounded-full"
                    />
                    <span className="dg-overflow">{tx.asset_ticker}</span>
                </Link>
            );
        },
    },
    {
        label: "USD Amount",
        slug: TopTransferColumns.usdAmount,
        sortsOn: TopTransferColumns.usdAmount,
        type: ColumnTypes.dollar,
        width: 150,
        render(row) {
            const formated = formatCurrencyValue(row.usd_amount);

            const explorerUrl = getBlockchainExplorerUrl({
                address: row.transaction_hash,
                networkSlug: row.network_slug,
                type: "tx",
            });

            const explorerName = EXPLORER_NAMES[row.network_slug as Networks];

            if (!explorerUrl) {
                return (
                    <div className="flex items-center gap-2">
                        <span>${formated}</span>
                        <ExternalLinkIcon className="w-4 h-4 invisible" />
                    </div>
                );
            }

            const tooltipCopy = `Open on ${explorerName}`;

            return (
                <div className="flex items-center gap-2">
                    <span>${formated}</span>

                    {explorerUrl && (
                        <Tooltip
                            placement="top"
                            tooltipContent={
                                <div className="w-max px-2 py-0.5 text-center">
                                    <p className="whitespace-nowrap text-xs font-light">
                                        {tooltipCopy}
                                    </p>
                                </div>
                            }
                        >
                            {({ setTriggerRef }) => (
                                <span
                                    ref={setTriggerRef}
                                    tabIndex={0}
                                    className="inline-flex items-center"
                                    aria-label="Show full date and time"
                                >
                                    <Anchor
                                        openInNewTab
                                        href={explorerUrl}
                                        analyticsLabel={
                                            TopTransfersUserEvents.BlockchainExplorerLinkClick
                                        }
                                        className="inline-flex items-center text-sm"
                                        variant="default"
                                    >
                                        <ExternalLinkIcon className="w-4 h-4" />
                                    </Anchor>
                                </span>
                            )}
                        </Tooltip>
                    )}
                </div>
            );
        },
    },
    {
        label: "Date",
        slug: TopTransferColumns.timestamp,
        sortsOn: TopTransferColumns.timestamp,
        width: 115,
        cellValue: (tx) => {
            return tx.timestamp;
        },
        type: ColumnTypes.datetimeCompact,
        // timeframe === "one_day"
        //     ? ColumnTypes.timeAgo
        //     : ColumnTypes.dateCompact,
    },
    {
        label: "Network",
        slug: TopTransferColumns.network,
        sortsOn: TopTransferColumns.network,
        type: ColumnTypes.string,
        width: 150,
        render: (tx) => {
            return (
                <Link
                    variant="default"
                    className="px-0.5 flex items-center"
                    analyticsLabel={TopTransfersUserEvents.NetworkLinkClick}
                    analyticsEventProperties={{
                        network: tx.network_slug,
                    }}
                    href={getNetworkURL({
                        networkSlug: tx.network_slug,
                    })}
                >
                    <NetworkBadge
                        network={{
                            slug: tx.network_slug,
                            name: tx.network_name,
                            icon_url: tx.network_icon_url,
                            icon_css: "h-4 w-4 min-w-4",
                        }}
                        enableTooltip={false}
                    />
                    <p className="ml-2 dg-overflow">{tx.network_name}</p>
                </Link>
            );
        },
    },
];

export function TopTransfersTable(props: { rows: TopTransfer[] }) {
    const { sortedItems, setQuery, query } = useInMemoryTopTransferSort({
        rows: [...props.rows],
    });

    return (
        <DataGrid
            rowKey="id"
            analyticsTableName="top-transfers"
            columns={
                columns as ColumnMetadata<TopTransfer, TopTransferColumns>[]
            }
            flush
            fixedHeader={false}
            bordered={false}
            size={RowSizes.extraSmall}
            watermark={true}
            query={query}
            loading={false}
            rows={sortedItems}
            enableScrollButton={false}
            enableScrollbar={false}
            disablePagination
            paginationState={{
                page: 1,
                perPage: 10,
                pageCount: 1,
                resultCount: props.rows.length,
            }}
            onChangeQuery={(updatedQuery) => {
                setQuery(updatedQuery);
            }}
            includeDummyRows={true}
        >
            {({ row, rowIndex, scrollLeft }) => (
                <DataGridRow
                    bordered={false}
                    row={row}
                    scrollLeft={scrollLeft}
                    columns={columns}
                    size={RowSizes.small}
                    striped={rowIndex % 2 === 1}
                />
            )}
        </DataGrid>
    );
}
